// ABOUTME: Data row renderer for ShDataGrid3 — a CSS-grid row that lays out visible cells in the resolved column order.
// ABOUTME: Dumb and lean: no branches, no memos, just map visibleRuntime → ShGridCell. Parent pre-filters visible columns.

import { memo } from "react";
import { Checkbox, Tooltip } from "@mui/material";
import cx from "classnames";
import ShGridCell from "./ShGridCell";

/**
 * ShGridRow
 *
 * Props:
 *   row                — the full row object
 *   rowIndex           — visible index in the current window
 *   visibleRuntime     — ORDERED, ALREADY-FILTERED array of runtime columns `[{ column, visible, pinned }, ...]`
 *                        Parent (ShDataGrid3) computes this once per render cycle and passes it down.
 *   codeColumnsByField — Map<field, codeColumn> for O(1) lookup of renderCell / headerName / etc.
 *   pinnedOffsets      — { [field]: { offsetPx, side } } computed once by parent via computePinnedOffsets
 *   gridTemplate       — CSS grid-template-columns string (shared across all rows for alignment)
 *   rowStyle           — extra inline style, used by the virtualizer for absolute positioning
 *   onRowClick         — (params) => void; called as `onRowClick({ id, row })`
 *   isSelected         — boolean; drives selected-row styling via className
 *   autoHeight         — boolean; when true the row uses content-fit height and hooks up the
 *                        virtualizer's measureElement so tanstack/react-virtual can track real
 *                        per-row heights (see ShGridBody for the wiring).
 *   measureRef         — ref callback from `virtualizer.measureElement`; only used when autoHeight
 *   rowTooltip         — optional hover text for the whole row, following the cursor
 */
const ShGridRow = memo(function ShGridRow({
    row,
    rowIndex,
    visibleRuntime,
    codeColumnsByField,
    pinnedOffsets,
    gridTemplate,
    rowStyle,
    onRowClick,
    isSelected = false,
    autoHeight = false,
    measureRef,
    selectable = false,
    onToggleRow,
    rowSelectable = true,
    rowClassName,
    rowTooltip,
}) {
    const handleClick = onRowClick && !row?.skeleton
        ? () => onRowClick({ id: row?.id, row })
        : undefined;

    const rowElement = (
        <div
            ref={autoHeight ? measureRef : undefined}
            data-index={autoHeight ? rowIndex : undefined}
            className={cx("sh-grid-row", rowClassName, {
                "sh-grid-row--selected": isSelected,
                "sh-grid-row--skeleton": !isSelected && row?.skeleton,
                "sh-grid-row--auto-height": autoHeight,
            })}
            data-row={rowIndex}
            onClick={handleClick}
            style={{
                ...rowStyle,
                display: "grid",
                gridTemplateColumns: gridTemplate,
                cursor: handleClick ? "pointer" : "default",
            }}
        >
            {selectable && (
                <div
                    className="sh-grid-cell sh-grid-cell--checkbox"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {!row?.skeleton && rowSelectable && (
                        <Checkbox
                            checked={isSelected}
                            onChange={() => onToggleRow?.(row?.id)}
                            sx={{ p: 0 }}
                        />
                    )}
                </div>
            )}
            {visibleRuntime.map((rc) => {
                const codeCol = codeColumnsByField.get(rc.column);
                if (!codeCol) return null;
                return (
                    <ShGridCell
                        key={rc.column}
                        column={codeCol}
                        row={row}
                        rowIndex={rowIndex}
                        pinnedOffset={pinnedOffsets?.[rc.column] ?? null}
                    />
                );
            })}
        </div>
    );

    return rowTooltip ? <Tooltip title={rowTooltip} followCursor>{rowElement}</Tooltip> : rowElement;
});

export default ShGridRow;
